/**
 * instacalc-data.js - Headless Data Layer
 *
 * Exposes a clean global API that any HTML template can consume.
 * This enables AI-generated prototypes to work with real calc data
 * by using simple data-* attributes.
 *
 * Usage:
 *   window.Instacalc.calcs          // Reactive array of all calcs
 *   window.Instacalc.selectedCalc   // Currently open calc
 *   window.Instacalc.createCalc()   // Create new calc
 *   window.Instacalc.openCalc(id)   // Open a calc
 *   window.Instacalc.on('change', fn) // Subscribe to changes
 */

(function() {
  'use strict';

  // Event emitter for reactivity
  const listeners = {};

  function emit(event, data) {
    const handlers = listeners[event] || [];
    handlers.forEach(fn => {
      try {
        fn(data);
      } catch (e) {
        console.warn('[Instacalc] Event handler error:', e);
      }
    });
  }

  // Proxy existing Calc/Action APIs
  function getCalcs() {
    if (typeof Calc === 'undefined') return [];
    try {
      return Calc.allByNewest().filter(c => !c.deleted);
    } catch (e) {
      return [];
    }
  }

  function getCurrentCalc() {
    if (typeof Action === 'undefined') return null;
    try {
      return Action.getCurrentCalc();
    } catch (e) {
      return null;
    }
  }

  function getRows() {
    const calc = getCurrentCalc();
    if (!calc || !calc.rows) return [];
    return calc.rows.map((row, index) => ({
      index,
      text: row.text || '',
      result: row.result,
      error: row.error,
      isComment: (row.text || '').trim().startsWith('//')
    }));
  }

  // The public API
  const Instacalc = {
    // ===== CALC LIST =====

    /** Get all calcs (non-deleted) */
    get calcs() {
      return getCalcs();
    },

    /** Get currently selected calc */
    get selectedCalc() {
      return getCurrentCalc();
    },

    /** Get selected calc ID */
    get selectedCalcId() {
      const calc = getCurrentCalc();
      return calc ? calc.id : null;
    },

    /** Create a new calc and optionally open it */
    createCalc(options = {}) {
      if (typeof Action === 'undefined') return null;
      const calc = Action.createAndOpen ? Action.createAndOpen() : null;
      emit('calcs:changed', { calcs: getCalcs() });
      return calc;
    },

    /** Open a calc by ID */
    openCalc(id) {
      if (typeof Action === 'undefined') return;
      if (Action.openCalc) {
        Action.openCalc(id);
        emit('calc:changed', { calc: getCurrentCalc() });
      }
    },

    /** Delete a calc by ID */
    deleteCalc(id) {
      if (typeof Action === 'undefined') return;
      if (Action.deleteCalc) {
        Action.deleteCalc(id);
        emit('calcs:changed', { calcs: getCalcs() });
      }
    },

    /** Rename a calc */
    renameCalc(id, newName) {
      if (typeof Action === 'undefined') return;
      const calc = Calc.get(id);
      if (calc && Action.updateName) {
        Action.updateName(calc, newName);
        emit('calcs:changed', { calcs: getCalcs() });
      }
    },

    // ===== CURRENT CALC (ROWS) =====

    /** Get rows of current calc */
    get rows() {
      return getRows();
    },

    /** Add a new row to current calc */
    addRow() {
      if (typeof Action === 'undefined') return;
      const calc = getCurrentCalc();
      if (calc && Action.addRow) {
        Action.addRow(calc);
        emit('calc:changed', { calc: getCurrentCalc() });
      }
    },

    /** Update row text */
    updateRow(index, text) {
      if (typeof Action === 'undefined') return;
      const calc = getCurrentCalc();
      if (calc && Action.updateRowText) {
        Action.updateRowText(calc, index, text);
        emit('calc:changed', { calc: getCurrentCalc() });
      }
    },

    /** Delete a row */
    deleteRow(index) {
      if (typeof Action === 'undefined') return;
      const calc = getCurrentCalc();
      if (calc && Action.deleteRow) {
        Action.deleteRow(calc, index);
        emit('calc:changed', { calc: getCurrentCalc() });
      }
    },

    /** Get result for a row */
    getResult(index) {
      const rows = getRows();
      if (index >= 0 && index < rows.length) {
        return rows[index].result;
      }
      return undefined;
    },

    // ===== SHARING =====

    /** Get current calc type: 'local' | 'shared' | 'private' | 'wiki' */
    get calcType() {
      const calc = getCurrentCalc();
      if (!calc) return 'local';
      if (calc.type) return calc.type;
      if (calc.published) return 'shared';
      return 'local';
    },

    /** Get public URL for current calc */
    getPublicURL() {
      if (typeof Action === 'undefined') return '';
      const calc = getCurrentCalc();
      if (!calc) return '';
      return Action.getPublicURL ? Action.getPublicURL(calc) : '';
    },

    /** Publish current calc (create public link) */
    publish() {
      if (typeof Action === 'undefined') return;
      const calc = getCurrentCalc();
      if (calc && Action.publish) {
        Action.publish(calc);
        emit('share:changed', { calc: getCurrentCalc() });
      }
    },

    /** Publish as specific mode: 'public' | 'private' | 'wiki' */
    publishAs(mode) {
      if (typeof Action === 'undefined') return;
      const calc = getCurrentCalc();
      if (!calc) return;

      // Check if Action.publishAs exists (may need to be implemented)
      if (Action.publishAs) {
        Action.publishAs(mode);
      } else if (mode === 'public' && Action.publish) {
        Action.publish(calc);
      }
      emit('share:changed', { calc: getCurrentCalc() });
    },

    /** Copy link to clipboard */
    copyLink() {
      const url = this.getPublicURL();
      if (!url) {
        // Auto-publish first if not published
        this.publish();
        // Wait a bit for publish to complete
        setTimeout(() => {
          const newUrl = this.getPublicURL();
          if (newUrl) this._copyToClipboard(newUrl);
        }, 500);
        return;
      }
      this._copyToClipboard(url);
    },

    _copyToClipboard(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      emit('link:copied', { url: text });
    },

    // ===== EVENTS =====

    /** Subscribe to events */
    on(event, callback) {
      if (!listeners[event]) {
        listeners[event] = [];
      }
      listeners[event].push(callback);
      return () => this.off(event, callback);
    },

    /** Unsubscribe from events */
    off(event, callback) {
      if (!listeners[event]) return;
      const idx = listeners[event].indexOf(callback);
      if (idx >= 0) {
        listeners[event].splice(idx, 1);
      }
    },

    /** Emit an event (for internal use) */
    emit(event, data) {
      emit(event, data);
    },

    // ===== INITIALIZATION =====

    /** Check if core dependencies are loaded */
    get isReady() {
      return typeof Calc !== 'undefined' && typeof Action !== 'undefined';
    },

    /** Wait for dependencies to be ready */
    whenReady(callback) {
      if (this.isReady) {
        callback();
        return;
      }
      const check = setInterval(() => {
        if (this.isReady) {
          clearInterval(check);
          callback();
        }
      }, 100);
    }
  };

  // Auto-subscribe to existing event system
  function setupEventBridge() {
    if (typeof InstacalcEvents === 'undefined') {
      setTimeout(setupEventBridge, 100);
      return;
    }

    // Bridge existing events to our system
    InstacalcEvents.on(InstacalcEvents.CALC_SWITCH, () => {
      emit('calc:selected', { calc: getCurrentCalc() });
      emit('calc:changed', { calc: getCurrentCalc() });
    });

    InstacalcEvents.on(InstacalcEvents.CALCS_UPDATED, () => {
      emit('calcs:changed', { calcs: getCalcs() });
    });
  }

  // Subscribe to Calc store changes via Event Bus (Legacy Calc.sub removed)
  function setupCalcSubscription() {
    if (typeof InstacalcEvents === 'undefined') {
      setTimeout(setupCalcSubscription, 100);
      return;
    }

    // Use CALC_CHANGED which fires on all saves/syncs
    InstacalcEvents.on(InstacalcEvents.CALC_CHANGED, () => {
      emit('calcs:changed', { calcs: getCalcs() });
    });

    // Also listen for bulk changes (e.g., Firebase sync, bulk import)
    InstacalcEvents.on(InstacalcEvents.CALCS_BULK_CHANGED, () => {
      emit('calcs:changed', { calcs: getCalcs() });
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupEventBridge();
      setupCalcSubscription();
    });
  } else {
    setupEventBridge();
    setupCalcSubscription();
  }

  // Expose globally
  window.Instacalc = Instacalc;

  if (window.Debug) window.Debug.log('ui', 'instacalc-data loaded - window.Instacalc API available');
})();
